import React from 'react';
import { InventorySchema, ResourceSchema, ResourceType, ToggleApplyModalOptions } from '../data/types';
import { ReactComponent as InventoryIcon } from '../icons/InventoryIcon.svg';
import {
  InventoryOutOfStockBadge,
  InventoryOutOfStockImage,
  formatInventoryRemainingLabel,
  inventoryOutOfStockCardClassName,
  inventoryOutOfStockDescription,
} from './InventoryOutOfStockElements';
import {
  getInventoryItemRemainingForSessions,
  isInventoryOutOfStockForSessions,
  type InventoryStockBooking,
  type InventoryTimeWindow,
} from '../utils/helpers/inventoryStockStatus';

export const formatResourceInventoryPrice = (inventory: InventorySchema) => {
  if (inventory.unit_price == null) return '$0';
  const unit = inventory.measure_unit ?? 'unit';
  const normalized = unit.toLowerCase();
  if (normalized.includes('hour') || normalized === 'hr' || normalized === 'hours') {
    return `$${inventory.unit_price}/hr`;
  }
  return `$${inventory.unit_price}/${unit}`;
};

export const getResourceInventoryDescription = (inventory: InventorySchema) =>
  inventory.category
    ? `${inventory.category.charAt(0).toUpperCase()}${inventory.category.slice(1)} item available for booking`
    : 'No description given for this item';

export const formatResourceInventoryUnitsLeft = (
  availableQuantity?: number,
  measureUnit?: string
) => {
  if (availableQuantity == null) return null;
  return formatInventoryRemainingLabel(0, availableQuantity, measureUnit);
};

type ResourceInventoryCardProps = {
  inventory: InventorySchema;
  loggedInUid?: string;
  setSignInModalIsOpen?: (isOpen: boolean) => void;
  toggleApplyModal?: (resourceType: ResourceType, options?: ToggleApplyModalOptions) => void;
  currentResource?: ResourceSchema;
  resourceBookings?: InventoryStockBooking[];
  sessionWindows?: InventoryTimeWindow[];
};

export const ResourceInventoryCard: React.FC<ResourceInventoryCardProps> = ({
  inventory,
  loggedInUid,
  setSignInModalIsOpen,
  toggleApplyModal,
  currentResource,
  resourceBookings,
  sessionWindows,
}) => {
  const remainingQuantity = getInventoryItemRemainingForSessions(
    inventory,
    resourceBookings,
    sessionWindows
  );
  const isOutOfStock = isInventoryOutOfStockForSessions({
    total_quantity: inventory.total_quantity,
    available_quantity: inventory.available_quantity,
    is_reusable: inventory.is_reusable,
    inventoryId: inventory.id,
    bookings: resourceBookings,
    sessions: sessionWindows,
  });

  const handleBookItem = () => {
    if (isOutOfStock) return;

    if (!loggedInUid) {
      setSignInModalIsOpen?.(true);
      return;
    }

    if (
      currentResource?.resource_type === 'farm' ||
      currentResource?.resource_type === 'kitchen'
    ) {
      toggleApplyModal?.(
        currentResource.resource_type,
        currentResource.resource_type === 'kitchen' ? { inventoryItem: inventory } : undefined
      );
    }
  };

  return (
    <div
      data-testid="resource-inventory-card"
      aria-disabled={isOutOfStock}
      className={`flex flex-row overflow-hidden rounded-xl border border-[#E9E9E9]/80 bg-white ${
        isOutOfStock ? inventoryOutOfStockCardClassName : 'cursor-pointer hover:bg-gray-50 hover:shadow-xs'
      }`}
      onClick={() => !isOutOfStock && handleBookItem()}
    >
      <div className="flex w-[120px] flex-shrink-0 self-stretch sm:w-[130px]">
        {isOutOfStock ? (
          <InventoryOutOfStockImage
            imageUrl={inventory.image_url}
            itemName={inventory.item_name}
            size="card"
          />
        ) : inventory.image_url ? (
          <img
            src={inventory.image_url}
            alt={inventory.item_name}
            className="h-full min-h-[132px] w-full rounded-l-xl object-cover"
          />
        ) : (
          <div className="flex h-full min-h-[132px] w-full items-center justify-center rounded-l-xl bg-[#ECECEC]">
            <InventoryIcon className="h-10 w-10 text-[#B0B0B0]" aria-hidden />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between p-4">
        <div>
          <h3
            className={`font-raleway text-[16px] font-bold ${
              isOutOfStock ? 'text-[#6B6B6B]' : 'text-black'
            }`}
          >
            {inventory.item_name}
          </h3>
          <p
            className={`mt-1 line-clamp-2 font-open-sans text-[14px] ${
              isOutOfStock ? 'text-[#9A9A9A]' : 'text-[#4D4D4D]'
            }`}
          >
            {isOutOfStock ? inventoryOutOfStockDescription : getResourceInventoryDescription(inventory)}
          </p>
          {isOutOfStock && <InventoryOutOfStockBadge size="desktop" />}
        </div>

        <div className="mt-3 flex flex-row flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 font-open-sans text-[14px]">
            <span className={`font-semibold ${isOutOfStock ? 'text-[#9A9A9A]' : 'text-black'}`}>
              {formatResourceInventoryPrice(inventory)}
            </span>
            {!isOutOfStock && (
              <>
                <span className="mx-2 text-[#C4C4C4]">·</span>
                <span className="text-[#9A9A9A]">
                  {formatResourceInventoryUnitsLeft(remainingQuantity, inventory.measure_unit)}
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            data-testid="resource-inventory-book-button"
            onClick={handleBookItem}
            disabled={isOutOfStock}
            className={`whitespace-nowrap border-0 rounded-lg px-4 py-2 font-raleway text-[14px] font-bold transition-colors sm:px-5 sm:py-2.5 sm:text-[15px] ${
              isOutOfStock
                ? 'cursor-not-allowed bg-[#ECECEC] text-[#9A9A9A]'
                : 'bg-[#013847] text-white hover:bg-[#013847]/90'
            }`}
          >
            {isOutOfStock ? 'Out of stock' : 'Book Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

type ResourceInventoryGridProps = {
  availableItems: InventorySchema[];
  loggedInUid?: string;
  setSignInModalIsOpen?: (isOpen: boolean) => void;
  toggleApplyModal?: (resourceType: ResourceType, options?: ToggleApplyModalOptions) => void;
  currentResource?: ResourceSchema;
  resourceBookings?: InventoryStockBooking[];
  sessionWindows?: InventoryTimeWindow[];
};

export const ResourceInventoryGrid: React.FC<ResourceInventoryGridProps> = ({
  availableItems,
  loggedInUid,
  setSignInModalIsOpen,
  toggleApplyModal,
  currentResource,
  resourceBookings,
  sessionWindows,
}) =>
  availableItems && availableItems.length > 0 ? (
    <div className="my-4 flex w-full flex-col font-open-sans">
      <div
        data-testid="resource-inventory-grid"
        className="grid w-full grid-cols-1 gap-4 md:grid-cols-3"
      >
        {availableItems.map((inventory, index) => (
          <ResourceInventoryCard
            key={inventory.id || inventory.item_name || index}
            inventory={inventory}
            loggedInUid={loggedInUid}
            setSignInModalIsOpen={setSignInModalIsOpen}
            toggleApplyModal={toggleApplyModal}
            currentResource={currentResource}
            resourceBookings={resourceBookings}
            sessionWindows={sessionWindows}
          />
        ))}
      </div>
    </div>
  ) : (
    <div className="my-4 flex h-auto w-full flex-col font-raleway text-[14px] text-[#aaa]">
      No inventory to show
    </div>
  );

export default ResourceInventoryGrid;
